import { useEffect, useRef, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { coffreCanShow, coffreLocked, coffreOpenKind, copyVaultFile, deleteCoffreItem, deletePageAndMedia, listCoffre, peekCoffreList, renameCoffreItem, type CoffreEntry } from "./coffre";
import { CoffreLockedLine, useCoffreUnlocked } from "./CoffreUnlock";
import { isNotesFile } from "./notesPad";
import { readCoffreVisit, writeCoffreVisit } from "./crmHome";
import { CoffreView } from "./CoffreView";
import type { CrmFiche } from "./types";
import { aimedFind, clearFind } from "./find";

export function CoffreBlock({ home, ownerId = 0, fiche, tapOpens = false }: { home: string; ownerId?: number; fiche: CrmFiche; tapOpens?: boolean }) {
  const [path, setPath] = useState(() => readCoffreVisit(fiche.id) ?? home);
  const [entries, setEntries] = useState<CoffreEntry[]>([]);
  const [error, setError] = useState("");
  const [locked, setLocked] = useState(false);
  const [busy, setBusy] = useState(true);
  const [file, setFile] = useState<CoffreEntry | null>(null);
  const [picked, setPicked] = useState("");
  const [view, setView] = useState<"grid" | "list">("grid");
  const [tick, setTick] = useState(0);
  const [drop, setDrop] = useState<CoffreEntry | null>(null);
  const [dropBusy, setDropBusy] = useState(false);
  const [menu, setMenu] = useState<{ x: number; y: number; entry: CoffreEntry } | null>(null);
  const [rename, setRename] = useState<{ entry: CoffreEntry; name: string; x: number; y: number } | null>(null);
  const [renameBusy, setRenameBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const skipClick = useRef(false);
  const hold = useRef(0);
  useCoffreUnlocked(() => setTick((value) => value + 1));

  useEffect(() => {
    setPath(readCoffreVisit(fiche.id) ?? home);
    setTick(0);
  }, [fiche.id, home]);

  useEffect(() => {
    const bump = () => setTick((value) => value + 1);
    window.addEventListener("crm-board-closed", bump);
    return () => window.removeEventListener("crm-board-closed", bump);
  }, []);

  useEffect(() => {
    let alive = true;
    const snap = tick > 0 ? null : peekCoffreList(path, false, ownerId);
    if (snap) {
      setEntries(snap);
      setBusy(false);
    } else if (tick === 0) {
      setEntries([]);
      setBusy(true);
    } else {
      setBusy(true);
    }
    setError("");
    void listCoffre(path, false, ownerId, tick > 0)
      .then((rows) => {
        if (alive) {
          setEntries(rows);
          setLocked(false);
        }
      })
      .catch((reason: unknown) => {
        if (!alive) return;
        if (path !== home) {
          writeCoffreVisit(fiche.id, home);
          setPath(home);
          return;
        }
        setLocked(coffreLocked(reason));
        setError(reason instanceof Error ? reason.message : "Le Coffre ne répond pas.");
      })
      .finally(() => {
        if (alive) setBusy(false);
      });
    return () => {
      alive = false;
    };
  }, [path, ownerId, tick, home, fiche.id]);

  useEffect(() => {
    const open = () => {
      const hit = aimedFind("Kanban", fiche.id);
      if (!hit?.boardPath) return;
      const parent = hit.boardPath.split("/").slice(0, -1).join("/") || home;
      if (path !== parent) {
        setPath(parent);
        return;
      }
      const entry = entries.find((row) => row.path === hit.boardPath);
      if (!entry) return;
      setFile(entry);
      clearFind();
    };
    open();
    window.addEventListener("crm-find", open);
    return () => window.removeEventListener("crm-find", open);
  }, [entries, path, fiche.id, home]);

  useEffect(() => {
    if (!menu) return;
    const close = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest("[data-coffre-menu]")) return;
      setMenu(null);
    };
    window.addEventListener("pointerdown", close);
    return () => window.removeEventListener("pointerdown", close);
  }, [menu]);

  useEffect(() => {
    if (!rename) return;
    const close = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest("[data-coffre-menu]")) return;
      setRename(null);
    };
    window.addEventListener("pointerdown", close);
    return () => window.removeEventListener("pointerdown", close);
  }, [rename]);

  function canAct(entry: CoffreEntry): boolean {
    return entry.kind !== "folder" && !isNotesFile(entry.name);
  }

  function isPage(name: string): boolean {
    const lower = name.toLowerCase();
    return lower.endsWith(".md") && !lower.endsWith(".board.md");
  }

  function canRename(entry: CoffreEntry): boolean {
    return entry.name.toLowerCase().endsWith(".md");
  }

  function titleOf(name: string): string {
    if (name.toLowerCase().endsWith(".board.md")) return name.replace(/\.board\.md$/i, "");
    return name.replace(/\.md$/i, "");
  }

  function openMenu(entry: CoffreEntry, x: number, y: number) {
    if (!canAct(entry)) return;
    setNotice("");
    setMenu({ x, y, entry });
  }

  async function remove(entry: CoffreEntry) {
    setDropBusy(true);
    setError("");
    try {
      if (isPage(entry.name)) await deletePageAndMedia(entry.path, ownerId);
      else await deleteCoffreItem(entry.path, ownerId);
      if (file?.path === entry.path) setFile(null);
      setDrop(null);
      setPicked("");
      setTick((value) => value + 1);
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Suppression impossible.");
    } finally {
      setDropBusy(false);
    }
  }

  async function commitRename(event: FormEvent) {
    event.preventDefault();
    if (!rename || renameBusy) return;
    const board = rename.entry.name.toLowerCase().endsWith(".board.md");
    const title = rename.name.trim().replace(/[\\/]/g, "").replace(/\.board\.md$/i, "").replace(/\.md$/i, "");
    if (!title) return;
    const next = board ? `${title}.board.md` : `${title}.md`;
    if (next === rename.entry.name) {
      setRename(null);
      return;
    }
    setRenameBusy(true);
    setError("");
    try {
      await renameCoffreItem(rename.entry.path, next, ownerId);
      if (file?.path === rename.entry.path) setFile(null);
      setRename(null);
      setPicked("");
      setTick((value) => value + 1);
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Renommage impossible.");
    } finally {
      setRenameBusy(false);
    }
  }

  async function copy(entry: CoffreEntry) {
    setMenu(null);
    setError("");
    setNotice("");
    try {
      await copyVaultFile(entry.path, entry.name, ownerId);
      setNotice("Copie créée à côté.");
      setTick((value) => value + 1);
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Duplication impossible.");
    }
  }

  function open(next: string) {
    if (next === path) return;
    setBusy(true);
    setPicked("");
    writeCoffreVisit(fiche.id, next);
    setPath(next);
  }

  const crumbs = path ? path.split("/").filter(Boolean) : [];
  const shown = entries.filter(coffreCanShow);
  const hidden = entries.length - shown.length;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <nav className="mb-2 flex flex-wrap items-center gap-1 text-xs text-slate-500">
        <button type="button" className="crm-ink font-semibold" onClick={() => open("")}>
          Coffre
        </button>
        {crumbs.map((name, index) => {
          const next = crumbs.slice(0, index + 1).join("/");
          const last = index === crumbs.length - 1;
          return (
            <span key={next} className="flex min-w-0 items-center gap-1">
              <span aria-hidden>/</span>
              {last ? (
                <span className="truncate font-medium text-slate-700">{name}</span>
              ) : (
                <button type="button" className="truncate hover:underline" onClick={() => open(next)}>
                  {name}
                </button>
              )}
            </span>
          );
        })}
        <span className="ml-auto flex shrink-0 items-center gap-1">
          {path !== home ? (
            <button type="button" className="crm-pill rounded-full px-2 py-0.5 text-[10px] font-semibold" onClick={() => open(home)}>
              Revenir à la fiche
            </button>
          ) : null}
          <button type="button" className="crm-pill grid h-6 w-6 place-items-center rounded-md" aria-label="Actualiser" title="Actualiser" onClick={() => setTick((value) => value + 1)}>
            <span className="material-icons-round text-[14px] leading-none" style={{ fontSize: 14, width: 14, height: 14 }}>refresh</span>
          </button>
          <button type="button" className={view === "grid" ? "crm-fill grid h-6 w-6 place-items-center rounded-md text-white" : "crm-pill grid h-6 w-6 place-items-center rounded-md"} aria-label="Vue grille" aria-pressed={view === "grid"} onClick={() => setView("grid")}>
            <GridIcon />
          </button>
          <button type="button" className={view === "list" ? "crm-fill grid h-6 w-6 place-items-center rounded-md text-white" : "crm-pill grid h-6 w-6 place-items-center rounded-md"} aria-label="Vue liste" aria-pressed={view === "list"} onClick={() => setView("list")}>
            <ListIcon />
          </button>
        </span>
      </nav>
      {locked ? <CoffreLockedLine /> : error ? <p className="text-sm text-rose-700">{error}</p> : null}
      {notice ? <p className="mb-2 text-xs text-slate-500">{notice}</p> : null}
      {drop ? (
        <div className="mb-2 rounded-xl border border-rose-200 bg-rose-50 px-2 py-2 text-xs text-rose-950">
          <p>{isPage(drop.name) ? `Supprimer « ${titleOf(drop.name)} » ? La page part dans la corbeille, ainsi que les photos et fichiers qu’elle est seule à utiliser.` : drop.name.toLowerCase().endsWith(".board.md") ? `Supprimer « ${titleOf(drop.name)} » ? Le kanban part dans la corbeille du Coffre.` : `Supprimer « ${titleOf(drop.name)} » ? Le fichier part dans la corbeille du Coffre.`}</p>
          <div className="mt-2 flex justify-end gap-2">
            <button type="button" className="rounded-full border border-rose-200 px-2 py-1 font-semibold" onClick={() => setDrop(null)}>Annuler</button>
            <button type="button" className="rounded-full bg-rose-600 px-2 py-1 font-semibold text-white disabled:opacity-50" disabled={dropBusy} onClick={() => void remove(drop)}>Supprimer</button>
          </div>
        </div>
      ) : null}
      <div className="relative min-h-0 flex-1">
      <ul className={view === "grid" ? "crm-scroll grid h-full auto-rows-min grid-cols-[repeat(auto-fill,minmax(5.75rem,1fr))] content-start gap-1 overflow-auto" : "crm-scroll flex h-full flex-col content-start overflow-auto"}>
        {shown.map((entry) => (
          <li key={entry.path}>
            {entry.kind === "folder" ? (
              <button type="button" className={view === "grid" ? "flex w-full flex-col items-center gap-1 rounded-xl px-1 py-2 text-center hover:bg-black/5" : "flex w-full items-center gap-2 rounded-lg px-1 py-1 text-left hover:bg-black/5"} onClick={() => open(entry.path)}>
                <span className={view === "list" ? "[&_svg]:h-6 [&_svg]:w-7" : ""}><FolderGlyph /></span>
                <span className={view === "grid" ? "line-clamp-2 w-full text-xs font-medium leading-tight" : "min-w-0 flex-1 truncate text-xs font-medium"}>{entry.name}</span>
              </button>
            ) : (
              <button
                type="button"
                className={(picked === entry.path ? "crm-soft " : "") + (view === "grid" ? "flex w-full flex-col items-center gap-1 rounded-xl px-1 py-2 text-center hover:bg-black/5" : "flex w-full items-center gap-2 rounded-lg px-1 py-1 text-left hover:bg-black/5")}
                onClick={() => { if (skipClick.current) { skipClick.current = false; return; } if (tapOpens) setFile(entry); else setPicked(entry.path); }}
                onDoubleClick={() => { if (!tapOpens) setFile(entry); }}
                onContextMenu={(event) => { event.preventDefault(); openMenu(entry, event.clientX, event.clientY); }}
                onTouchStart={(event) => { if (!canAct(entry)) return; const touch = event.touches[0]; if (!touch) return; window.clearTimeout(hold.current); hold.current = window.setTimeout(() => { skipClick.current = true; openMenu(entry, touch.clientX, touch.clientY); }, 550); }}
                onTouchEnd={() => window.clearTimeout(hold.current)}
                onTouchMove={() => window.clearTimeout(hold.current)}
              >
                <span className={view === "list" ? "[&_svg]:h-6 [&_svg]:w-5" : ""}><FileGlyph name={entry.name} /></span>
                <span className={view === "grid" ? "line-clamp-2 w-full text-xs leading-tight" : "min-w-0 flex-1 truncate text-xs"}>{titleOf(entry.name)}</span>
                <span className={view === "grid" ? "text-[10px] text-slate-400" : "shrink-0 text-[10px] text-slate-400"}>{formatSize(entry.size)}</span>
              </button>
            )}
          </li>
        ))}
      </ul>
      {!busy && !error && shown.length === 0 ? <p className="px-1 text-sm text-slate-500">{hidden > 0 ? "Aucun fichier ouvrable ici." : "Dossier vide."}</p> : null}
      {busy ? (
        <div className="crm-browse-loading" role="status" aria-live="polite">
          <span className="crm-browse-loading-spin" aria-hidden />
          <span>Chargement en cours…</span>
        </div>
      ) : null}
      </div>
      {menu ? createPortal(
        <div data-coffre-menu className="fixed z-[80] min-w-36 rounded-xl border border-slate-200 bg-white p-1 text-sm shadow-xl" style={{ left: Math.min(menu.x, window.innerWidth - 170), top: Math.min(menu.y, window.innerHeight - 132) }}>
          {canRename(menu.entry) ? <button type="button" className="block w-full rounded-lg px-3 py-1.5 text-left hover:bg-slate-50" onClick={() => { setRename({ entry: menu.entry, name: titleOf(menu.entry.name), x: menu.x, y: menu.y }); setMenu(null); }}>Renommer</button> : null}
          <button type="button" className="block w-full rounded-lg px-3 py-1.5 text-left hover:bg-slate-50" onClick={() => void copy(menu.entry)}>Dupliquer</button>
          {ownerId > 0 ? null : <button type="button" className="block w-full rounded-lg px-3 py-1.5 text-left font-semibold text-rose-700 hover:bg-rose-50" onClick={() => { setDrop(menu.entry); setMenu(null); }}>Supprimer</button>}
        </div>,
        document.body,
      ) : null}
      {rename ? createPortal(
        <form data-coffre-menu className="fixed z-[80] w-56 rounded-xl border border-slate-200 bg-white p-2 text-sm shadow-xl" style={{ left: Math.min(rename.x, window.innerWidth - 240), top: Math.min(rename.y, window.innerHeight - 120) }} onSubmit={(event) => void commitRename(event)}>
          <input className="w-full rounded-lg border border-slate-200 px-2 py-1" value={rename.name} autoFocus onChange={(event) => setRename({ ...rename, name: event.target.value })} />
          <div className="mt-2 flex justify-end gap-2">
            <button type="button" className="rounded-full px-2 py-1 text-xs font-semibold text-slate-500" onClick={() => setRename(null)}>Annuler</button>
            <button type="submit" className="rounded-full crm-fill px-3 py-1 text-xs font-semibold text-white disabled:opacity-50" disabled={renameBusy || rename.name.trim() === ""}>Renommer</button>
          </div>
        </form>,
        document.body,
      ) : null}
      {file && coffreOpenKind(file.name) ? <CoffreView entry={file} kind={coffreOpenKind(file.name)!} ownerId={ownerId} fiche={fiche} onClose={() => { setFile(null); setTick((value) => value + 1); }} /> : null}
    </div>
  );
}

function GridIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" aria-hidden>
      <path fill="currentColor" d="M1 1h6v6H1V1Zm8 0h6v6H9V1ZM1 9h6v6H1V9Zm8 0h6v6H9V9Z" />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" aria-hidden>
      <path fill="currentColor" d="M1 2.5h14v1.6H1V2.5Zm0 4.7h14v1.6H1V7.2Zm0 4.7h14V13.5H1v-1.6Z" />
    </svg>
  );
}

function FolderGlyph() {
  return (
    <svg viewBox="0 0 48 40" className="h-10 w-12" aria-hidden>
      <path d="M4 10.5A4.5 4.5 0 0 1 8.5 6h10.2c1 0 2 .4 2.7 1.1L24 10h15.5A4.5 4.5 0 0 1 44 14.5v17A4.5 4.5 0 0 1 39.5 36h-31A4.5 4.5 0 0 1 4 31.5v-21Z" fill="var(--crm-accent, #3b82f6)" />
      <path d="M4 16h40v15.5A4.5 4.5 0 0 1 39.5 36h-31A4.5 4.5 0 0 1 4 31.5V16Z" fill="var(--crm-accent-strong, #2563eb)" />
    </svg>
  );
}

function FileGlyph({ name }: { name: string }) {
  const lower = name.toLowerCase();
  if (lower.endsWith(".board.md")) return <KanbanGlyph />;
  if (lower.endsWith(".md")) return <PageGlyph />;
  const ext = name.includes(".") ? name.slice(name.lastIndexOf(".") + 1).slice(0, 4).toUpperCase() : "";
  const tone = fileTone(name);
  return (
    <svg viewBox="0 0 40 48" className="h-11 w-9" aria-hidden>
      <path d="M6 4h18l10 10v28a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" fill={tone} />
      <path d="M24 4v10h10" fill="#000" opacity="0.18" />
      {ext ? (
        <text x="20" y="33" textAnchor="middle" fontSize="8" fontWeight="700" fill="#fff">
          {ext}
        </text>
      ) : null}
    </svg>
  );
}

function PageGlyph() {
  return (
    <svg viewBox="0 0 40 48" className="h-11 w-9" aria-hidden>
      <path d="M6 4h18l10 10v28a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" fill="#3b82f6" />
      <path d="M24 4v10h10" fill="#000" opacity="0.18" />
      <path d="M11 24h18M11 30h14M11 36h10" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function KanbanGlyph() {
  return (
    <svg viewBox="0 0 40 48" className="h-11 w-9" aria-hidden>
      <rect x="3" y="4" width="34" height="40" rx="6" fill="#0f766e" />
      <rect x="7" y="9" width="7" height="22" rx="1.5" fill="#fff" opacity="0.95" />
      <rect x="16.5" y="9" width="7" height="14" rx="1.5" fill="#fff" opacity="0.75" />
      <rect x="26" y="9" width="7" height="18" rx="1.5" fill="#fff" opacity="0.55" />
    </svg>
  );
}

function fileTone(name: string): string {
  const ext = name.includes(".") ? name.slice(name.lastIndexOf(".") + 1).toLowerCase() : "";
  if (ext === "pdf") return "#e5252a";
  if (["doc", "docx", "odt", "rtf"].includes(ext)) return "#2b579a";
  if (["xls", "xlsx", "xlsm", "ods", "csv"].includes(ext)) return "#217346";
  if (["ppt", "pptx", "odp"].includes(ext)) return "#d24726";
  if (["jpg", "jpeg", "png", "gif", "webp", "bmp", "tif", "tiff", "heic", "svg"].includes(ext)) return "#7c3aed";
  if (ext === "eml") return "#4f46e5";
  return "#64748b";
}

function formatSize(size: number | null | undefined): string {
  if (size == null) return "";
  if (size < 1024) return `${size} o`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} Ko`;
  return `${(size / (1024 * 1024)).toFixed(1)} Mo`;
}
